import React, { useMemo, useState } from 'react';
import { ActivityIndicator, Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop } from 'react-native-svg';

import ChipPng from '../../../../assets/chip.png';
import ExperiencePng from '../../../../assets/ranking/RankExp.png';
import { useSvgId } from '../../../../utils/hooks/useSvgId';
import { Pressable } from '../../common/Touchable';

// Geometry from the Figma component "Daily Mission" (2944:10901 and its state variants), a
// 342x56 row on the 366-wide Daily Missions modal. Every offset is scaled from these so the row
// keeps its proportions at whatever width the modal hands it.
export const MISSION_ROW_WIDTH = 342;
export const MISSION_ROW_HEIGHT = 56;
const PROGRESS_LABEL_WIDTH = 54;
// Counts longer than 9 characters ("2,500/5,000") set smaller instead of clipping: web ignores
// adjustsFontSizeToFit, so the size is picked from the length up front.
const PROGRESS_LABEL_FIT_CHARS = 9;
const PROGRESS_LABEL_FONT_SIZE = 12;

// The design sets the mission title and the disabled action label in Sofia Sans Condensed.
const FONT = 'Sofia Sans';
const FONT_CONDENSED = 'Sofia Sans Condensed';

const colors = {
	title: '#FFFFFF',
	accent: '#FFFF99',
	progressTrack: '#4A1A0E',
	progressGold: '#FFC500',
	progressDone: '#0CEB66',
	divider: 'rgba(255,178,26,0.3)',
	pendingPlate: '#330E07',
	pendingBorder: '#DA600E',
	claimableBorder: '#FCF17B',
	claimBorder: 'rgba(184,255,140,0.75)',
} as const;

// Both plates are vertical gradients, so they are painted with an SVG rect the way the
// tournament card paints its button - the app has no gradient view library.
const CLAIMABLE_PLATE: Array<[string, string]> = [
	['0', '#770700'],
	['0.80127', '#320400'],
	['1', '#320400'],
];
const CLAIM_BUTTON: Array<[string, string]> = [
	['0', '#66D93D'],
	['0.55', '#248F1C'],
	['1', '#0D540F'],
];

export type DailyMission = {
	id: string;
	title: string;
	/** Steps done so far, e.g. 7 of 10 hands. */
	progress: number;
	/** Steps the mission needs, e.g. 10 hands. */
	goal: number;
	/** "7/10" - kept separate because the design writes chip goals as "5,000/5,000". */
	progressLabel: string;
	rewardLabel: string | null;
	rewardIcon: 'chips' | 'experience' | null;
	claimable: boolean;
	claimed: boolean;
	busy: boolean;
	/** Drawn at whatever size the row resolves to, so the art scales with the modal. */
	renderArtwork: (size: number) => React.ReactNode;
};

type Props = {
	mission: DailyMission;
	claimLabel: string;
	claimedLabel: string;
	/** Keep room for the CLAIM button even on rows without one, so every row's bar lines up. */
	reserveAction: boolean;
	/** Width the 342-wide design row should occupy. */
	width: number;
	onClaim: (mission: DailyMission) => void;
};

/**
 * One mission line: artwork, title over a progress bar and its count, then the CLAIM button and,
 * past a divider, the reward. A finished mission turns its bar green and gets the CLAIM button.
 */
export const DailyMissionRow = ({
	mission,
	claimLabel,
	claimedLabel,
	reserveAction,
	width,
	onClaim,
}: Props) => {
	const scale = width / MISSION_ROW_WIDTH;
	const claimable = mission.claimable;
	const finished = mission.claimable || mission.claimed;
	const plateId = useSvgId('missionPlate');
	const buttonId = useSvgId('missionAction');
	const g = useMemo(() => createGeometry(scale), [scale]);
	const styles = useMemo(() => createStyles(scale), [scale]);
	const [trackWidth, setTrackWidth] = useState(0);

	const progressFontSize =
		PROGRESS_LABEL_FONT_SIZE *
		scale *
		Math.min(1, PROGRESS_LABEL_FIT_CHARS / mission.progressLabel.length);

	const filled = mission.goal > 0 ? Math.min(mission.progress / mission.goal, 1) : 0;

	return (
		<View style={[styles.row, finished ? styles.rowClaimable : styles.rowPending]}>
			{finished ? (
				<View pointerEvents='none' style={[styles.plate, { borderRadius: g.radius }]}>
					<Svg width='100%' height='100%'>
						<Defs>
							<LinearGradient id={plateId} x1='0' y1='0' x2='0' y2='1'>
								{CLAIMABLE_PLATE.map(([offset, color]) => (
									<Stop key={offset} offset={offset} stopColor={color} />
								))}
							</LinearGradient>
						</Defs>
						<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${plateId})`} />
					</Svg>
				</View>
			) : null}

			<View style={styles.artwork}>{mission.renderArtwork(g.artworkSize)}</View>

			<View style={styles.details}>
				<Text numberOfLines={1} style={styles.title}>
					{mission.title}
				</Text>
				<View style={styles.progressRow}>
					<View
						style={styles.progressTrack}
						onLayout={e => setTrackWidth(e.nativeEvent.layout.width)}
					>
						<View
							style={[
								styles.progressFill,
								{
									width: trackWidth * filled,
									backgroundColor: finished ? colors.progressDone : colors.progressGold,
								},
							]}
						/>
					</View>
					<Text numberOfLines={1} style={[styles.progressLabel, { fontSize: progressFontSize }]}>
						{mission.progressLabel}
					</Text>
				</View>
			</View>

			{/* The action slot is kept on every row while any row needs it, so the bars - and the counts
			    after them - line up down the list. */}
			{reserveAction ? (
				<View style={styles.actionSlot}>
					{claimable ? (
						<Pressable
							disabled={mission.busy}
							onPress={() => onClaim(mission)}
							style={[styles.action, styles.actionClaim]}
						>
							<View pointerEvents='none' style={[styles.plate, { borderRadius: g.actionRadius }]}>
								<Svg width='100%' height='100%'>
									<Defs>
										<LinearGradient id={buttonId} x1='0' y1='0' x2='0' y2='1'>
											{CLAIM_BUTTON.map(([offset, color]) => (
												<Stop key={offset} offset={offset} stopColor={color} />
											))}
										</LinearGradient>
									</Defs>
									<Rect x='0' y='0' width='100%' height='100%' fill={`url(#${buttonId})`} />
								</Svg>
							</View>
							{mission.busy ? (
								<ActivityIndicator color={colors.title} />
							) : (
								<Text
									numberOfLines={1}
									adjustsFontSizeToFit
									minimumFontScale={0.7}
									style={styles.claimLabel}
								>
									{claimLabel}
								</Text>
							)}
						</Pressable>
					) : mission.claimed ? (
						<Text
							numberOfLines={1}
							adjustsFontSizeToFit
							minimumFontScale={0.7}
							style={styles.claimedLabel}
						>
							{claimedLabel}
						</Text>
					) : null}
				</View>
			) : null}

			<View style={styles.divider} />

			<View style={styles.reward}>
				{mission.rewardLabel ? (
					<>
						<Image
							source={mission.rewardIcon === 'experience' ? ExperiencePng : ChipPng}
							style={styles.rewardChip}
							resizeMode='contain'
						/>
						<Text
							numberOfLines={1}
							adjustsFontSizeToFit
							minimumFontScale={0.7}
							style={styles.rewardValue}
						>
							{mission.rewardLabel}
						</Text>
					</>
				) : null}
			</View>
		</View>
	);
};

const createGeometry = (scale: number) => ({
	radius: 12 * scale,
	actionRadius: 8 * scale,
	artworkSize: 36 * scale,
});

const createStyles = (scale: number) => {
	const s = (value: number) => value * scale;

	return StyleSheet.create({
		row: {
			width: s(MISSION_ROW_WIDTH),
			height: s(MISSION_ROW_HEIGHT),
			borderRadius: s(12),
			borderWidth: Math.max(0.5, s(1)),
			paddingLeft: s(8),
			paddingRight: s(6),
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(8),
		},
		rowPending: {
			backgroundColor: colors.pendingPlate,
			borderColor: colors.pendingBorder,
			shadowColor: '#FFB21A',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.55,
			shadowRadius: s(2.5),
			elevation: 3,
		},
		rowClaimable: {
			// The plate under it paints the gradient; this keeps the iOS shadow layer opaque.
			backgroundColor: '#320400',
			borderColor: colors.claimableBorder,
			shadowColor: '#FF7200',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.7,
			shadowRadius: s(5),
			elevation: 6,
		},
		plate: {
			...StyleSheet.absoluteFillObject,
			overflow: 'hidden',
		},
		// The artwork is 36 square in the design and overhangs its 32-wide slot by 2 a side.
		artwork: {
			width: s(32),
			height: s(36),
			alignItems: 'center',
			justifyContent: 'center',
		},
		details: {
			flex: 1,
			gap: s(4),
		},
		title: {
			fontFamily: FONT_CONDENSED,
			fontSize: s(16),
			lineHeight: s(19.2),
			fontWeight: '600',
			color: colors.title,
		},
		progressRow: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: s(6),
			height: s(14),
		},
		progressTrack: {
			flex: 1,
			height: s(8),
			borderRadius: s(4),
			backgroundColor: colors.progressTrack,
			overflow: 'hidden',
		},
		progressFill: {
			height: s(8),
			borderRadius: s(4),
		},
		// A fixed, centred column: the counts stack straight down the list whatever their length.
		progressLabel: {
			width: s(PROGRESS_LABEL_WIDTH),
			fontFamily: FONT,
			lineHeight: s(14.4),
			fontWeight: '600',
			color: colors.title,
			textAlign: 'center',
		},
		actionSlot: {
			width: s(68),
			alignItems: 'center',
			justifyContent: 'center',
		},
		action: {
			width: s(68),
			height: s(36),
			borderRadius: s(8),
			borderWidth: Math.max(0.5, s(1)),
			alignItems: 'center',
			justifyContent: 'center',
			paddingHorizontal: s(2),
			overflow: 'hidden',
		},
		actionClaim: {
			borderColor: colors.claimBorder,
			shadowColor: '#40FF33',
			shadowOffset: { width: 0, height: 0 },
			shadowOpacity: 0.16,
			shadowRadius: s(1.5),
		},
		claimLabel: {
			fontFamily: FONT,
			fontSize: s(14),
			lineHeight: s(16),
			fontWeight: '700',
			color: colors.title,
			letterSpacing: s(0.05),
		},
		claimedLabel: {
			fontFamily: FONT_CONDENSED,
			fontSize: s(12),
			lineHeight: s(14.4),
			fontWeight: '900',
			color: colors.title,
			opacity: 0.65,
			letterSpacing: s(0.48),
			textTransform: 'uppercase',
		},
		divider: {
			width: Math.max(1, s(1)),
			height: s(36),
			backgroundColor: colors.divider,
		},
		reward: {
			width: s(44),
			alignItems: 'center',
			gap: s(2),
		},
		rewardChip: {
			width: s(22),
			height: s(22),
		},
		rewardValue: {
			fontFamily: FONT,
			fontSize: s(13),
			lineHeight: s(16.8),
			fontWeight: '600',
			color: colors.title,
			letterSpacing: s(0.12),
		},
	});
};

export default DailyMissionRow;
